import { Box, Button, Link, Stack, TextField } from '@mui/material';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import AuthFrame from '@/components/auth/AuthFrame';
import useAuth from '@/hooks/useAuth';
import { signUp, toAuthApiError } from '@/services/authApi';
import { URL } from '@/types';
import { keepPendingPassword } from '@/utils/pendingPassword';

type SignUpForm = { email: string; password: string; passwordConfirm: string };

export default function SignUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { validationRules, errorMessage } = useAuth();
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const { control, handleSubmit, watch } = useForm<SignUpForm>({
    defaultValues: { email: '', password: '', passwordConfirm: '' },
    mode: 'onChange',
  });
  const password = watch('password');

  const onSubmit = async ({ email, password }: SignUpForm) => {
    setError(undefined);
    setSubmitting(true);
    try {
      await signUp(email, password);
      // 確認コード後の自動ログイン用。history.state には載せない（utils/pendingPassword 参照）
      keepPendingPassword(email, password);
      navigate(URL.SIGN_UP_CONFIRM, { state: { email } });
    } catch (e) {
      setError(errorMessage(toAuthApiError(e).code));
      setSubmitting(false);
    }
  };

  return (
    <AuthFrame title={t('pages:auth:sign_up:title')} error={error}>
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} sx={{ mb: 4 }}>
          <Controller
            name="email"
            control={control}
            rules={validationRules.email}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                type="email"
                autoComplete="email"
                label={t('pages:auth:labels:email')}
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={validationRules.password}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                type="password"
                autoComplete="new-password"
                label={t('pages:auth:labels:password')}
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message ?? t('pages:auth:messages:password_rule')}
              />
            )}
          />
          <Controller
            name="passwordConfirm"
            control={control}
            rules={validationRules.passwordConfirm(password)}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                type="password"
                autoComplete="new-password"
                label={t('pages:auth:labels:password_confirm')}
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Button type="submit" color="primary" variant="contained" disabled={submitting}>
            {t('pages:auth:sign_up:submit')}
          </Button>
        </Box>
        <Stack alignItems="center">
          <Link component={RouterLink} to={URL.LOGIN}>
            {t('pages:auth:sign_up:login')}
          </Link>
        </Stack>
      </Box>
    </AuthFrame>
  );
}
